import Big from 'big.js';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';

import { DailyDeploysChart } from '@block-explorer/charts';
import {
  DEFAULT_AMOUNT_PRECISION,
  FlexColumn,
  FlexRow,
  formatNumber,
  formatPercentage,
  formatTimestampAge,
  InfoIcon,
  useMatchMedia,
} from '@make-software/cspr-design';
import {
  BodyText,
  Button,
  HeaderText,
  OrderDirection,
  Tooltip,
} from '@block-explorer/ui';

import { useGetMetrics } from '../../hooks';
import {
  useCurrencyRateAmount,
  useGetPaginatedDailyDeployNumbers,
} from '../../hooks/event-store-hooks';
import FiatCurrency from '../../components/multi-currency/multi-currency';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { InfoSvgIcon } from '../../components';
import { ANALYTICS_PATH } from '../../router/paths';
import { useCustomHistory } from '../../router/navigation';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { getMetricsPercentage } from './utils.tsx';

const Container = styled.div(({ theme }) =>
  theme.withMedia({
    height: '100%',
    background: theme.styleguideColors.backgroundPrimary,
    boxShadow: theme.boxShadow.block,
    borderRadius: theme.borderRadius.base,
    padding: ['24px 12px', '28px 28px', '28px 28px', '32px 28px'],
    width: '100%',
    display: 'flex',
    flexDirection: ['column', 'column', 'row'],
    justifyContent: ['center', 'center', 'flex-start'],
    alignItems: 'center',
    '> * + *': {
      marginTop: [20, 20, 0],
      paddingTop: [19, 19, 0],
      marginLeft: [0, 0, 12],
      borderTop: [theme.border.separator, theme.border.separator, 'none'],
    },
  })
);

const MetricMainRow = styled.div(({ theme }) =>
  theme.withMedia({
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: ['column'],
    '> * + *': {
      marginTop: [20, 24],
      paddingTop: [19, 23],
      borderTop: theme.border.separator,
    },
  })
);

const MetricRow = styled.div(({ theme }) =>
  theme.withMedia({
    width: '100%',
    display: 'flex',
    flexDirection: ['column', 'row'],
    justifyContent: ['center', 'flex-start'],
    alignItems: 'center',
    '> * + *': {
      marginTop: [20, 0],
      paddingTop: [19, 0],
      borderTop: [theme.border.separator, 'none'],
    },
  })
);

const MetricBlock = styled.div(({ theme }) =>
  theme.withMedia({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: ['center', 'flex-start'],
    '> * + *': {
      marginTop: [4],
    },
  })
);

const ChartBlock = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    width: ['100%', '100%', 325],
    flexDirection: 'column',
    flexShrink: 0,
    alignItems: ['center', 'center', 'flex-start'],
  })
);

const StyledButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: ['100%', '100%', '140'],
  })
);

const HomeMetrics = React.memo(() => {
  const { data: metricsData } = useGetMetrics();
  const { t } = useTranslation();

  const screenType = useMatchMedia(['mobile', 'tabletOrAbove'], []);
  const btnHeight = useMatchMedia<any>(['36', '36', '24'], []);

  const yesterdayTimeStamp = new Date().getTime() - 24 * 60 * 60 * 1000;
  const yesterday = new Date(endOfDay(yesterdayTimeStamp));
  const someDaysAgo = new Date(startOfDay(subDays(yesterday, 13)));

  const { apyPercent, eraTotalStakePercent, circulatingSupplyPercent } =
    getMetricsPercentage(metricsData);

  const history = useCustomHistory();
  const currency = useSelector(
    (state: RootState) => state.currencies.selectedCurrency
  );

  const { data: rateData, loading: loadingRateData } = useCurrencyRateAmount({
    currencyCode: currency.id.toString(),
  });

  const { data: dailyDeployNumbers, loading: loadingDailyDeployNumbers } =
    useGetPaginatedDailyDeployNumbers({
      from: someDaysAgo.toISOString(),
      to: yesterday.toISOString(),
      order_direction: OrderDirection.ASC,
      order_by: 'date',
    });

  const csprCurrencyRate = rateData?.data?.amount.toString();
  const marketCap =
    metricsData?.circulatingSupply == null || !csprCurrencyRate
      ? null
      : Big(metricsData.circulatingSupply).mul(csprCurrencyRate).toString();

  const dailyDeploysSeries = dailyDeployNumbers?.data;
  const accTotalDeploys =
    (dailyDeploysSeries &&
      dailyDeploysSeries[dailyDeploysSeries.length - 1]
        ?.total_contract_calls_num +
        dailyDeploysSeries[dailyDeploysSeries.length - 1]
          ?.total_native_transfers_num) ||
    0;

  const isLoading =
    metricsData == null ||
    rateData == null ||
    loadingRateData ||
    dailyDeployNumbers == null ||
    loadingDailyDeployNumbers;

  return (
    <Container>
      <MetricMainRow>
        <MetricRow>
          <MetricBlock>
            <BodyText size={2} variation="darkGray" uppercase>
              <Trans t={t}>Block Height</Trans>
            </BodyText>
            <HeaderText size={2} loading={isLoading}>
              {metricsData && formatNumber(metricsData.blockHeight)}
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              {metricsData &&
                formatTimestampAge(metricsData.blockHeightTimestamp)}
            </BodyText>
          </MetricBlock>
          <MetricBlock>
            <BodyText size={2} variation="darkGray" uppercase>
              <Trans t={t}>Current Validators</Trans>
            </BodyText>
            <HeaderText size={2} loading={isLoading}>
              {metricsData && metricsData.activeValidatorsCount}
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              {metricsData && metricsData.allActiveBidsCount}{' '}
              <Trans t={t}>active bids</Trans>
            </BodyText>
          </MetricBlock>
          <MetricBlock>
            <BodyText size={2} variation="darkGray" uppercase>
              <Trans t={t}>Total Stake Bonded</Trans>
            </BodyText>
            <HeaderText size={2} loading={isLoading}>
              {metricsData && formatNumber(metricsData.eraTotalStake)}
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              {eraTotalStakePercent &&
                formatNumber(eraTotalStakePercent, { precision: 2 })}
              % <Trans t={t}>of Total Supply</Trans>
            </BodyText>
          </MetricBlock>
        </MetricRow>

        <MetricRow>
          <MetricBlock>
            <BodyText size={2} variation="darkGray" uppercase>
              <Trans t={t}>CSPR Price</Trans>
            </BodyText>
            <HeaderText size={2} loading={isLoading}>
              <FiatCurrency
                amount={csprCurrencyRate}
                precision={DEFAULT_AMOUNT_PRECISION}
              />
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              <FiatCurrency amount={marketCap} precision={0} />{' '}
              <Trans t={t}>Market Cap</Trans>
            </BodyText>
          </MetricBlock>
          <MetricBlock>
            <BodyText size={2} variation="darkGray" uppercase>
              <Trans t={t}>Circulating Supply</Trans>
            </BodyText>
            <HeaderText size={2} loading={isLoading}>
              {metricsData && formatNumber(metricsData.circulatingSupply)}
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              {circulatingSupplyPercent &&
                formatPercentage(circulatingSupplyPercent, { precision: 1 })}
              % <Trans t={t}>of</Trans>{' '}
              {metricsData && `${formatNumber(metricsData.totalSupply)}`}{' '}
              <Trans t={t}>Total</Trans>
            </BodyText>
          </MetricBlock>
          <MetricBlock>
            <FlexRow align={'center'}>
              <BodyText size={2} variation="darkGray" uppercase>
                <Trans t={t}>APY</Trans>
              </BodyText>
              <Tooltip
                tooltipContent={t(
                  "Expected annual percentage yield on the staked tokens without taking the validator's fee into account"
                )}
              >
                <InfoSvgIcon src={InfoIcon} style={{ marginLeft: 6 }} />
              </Tooltip>
            </FlexRow>
            <HeaderText size={2} loading={isLoading}>
              {apyPercent && formatNumber(apyPercent, { precision: 2 })}%
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              <Trans t={t}>Annual Percentage Yield</Trans>
            </BodyText>
          </MetricBlock>
        </MetricRow>
      </MetricMainRow>
      <ChartBlock>
        {screenType === 'mobile' ? (
          <FlexColumn itemsSpacing={32}>
            <MetricBlock>
              <BodyText size={2} variation="darkGray" uppercase>
                {t('TOTAL TRANSACTIONS')}
              </BodyText>
              <HeaderText size={2} loading={isLoading}>
                {accTotalDeploys && formatNumber(accTotalDeploys)}
              </HeaderText>
            </MetricBlock>
            <BodyText size={2} variation="darkGray" uppercase noWrap>
              {t('14-DAY TRANSACTIONS')}
            </BodyText>
          </FlexColumn>
        ) : (
          <FlexRow
            justify={screenType === 'mobile' ? 'center' : 'space-between'}
            style={{ width: '100%' }}
          >
            <BodyText size={2} variation="darkGray" uppercase noWrap>
              {t('14-DAY TRANSACTIONS')}
            </BodyText>
            <FlexRow>
              <BodyText size={2} variation="darkGray" uppercase>
                <Trans t={t}>TOTAL</Trans>
              </BodyText>
              <BodyText
                size={1}
                variation="black"
                uppercase
                loading={isLoading}
              >
                &nbsp; {accTotalDeploys && formatNumber(accTotalDeploys)}
              </BodyText>
            </FlexRow>
          </FlexRow>
        )}
        {isLoading ? (
          <span>
            <Skeleton width={324} style={{ fontSize: '180px' }} />
          </span>
        ) : (
          <>
            <DailyDeploysChart
              series={[
                {
                  name: t('Number of transactions'),
                  data: dailyDeploysSeries,
                },
              ]}
              xAxisGetter={(i) => i.date}
              yAxisGetter={(i) => i.contract_calls_num + i.native_transfer_num}
            />
            <StyledButton
              height={btnHeight}
              lineHeight={'sm'}
              color={'secondaryRed'}
              onClick={() => {
                history.push(ANALYTICS_PATH);
              }}
            >
              {t('View more analytics')}
            </StyledButton>
          </>
        )}
      </ChartBlock>
    </Container>
  );
});

export default HomeMetrics;
